import React, {
  useEffect,
  useState,
} from "react";

import {
  X,
  Plus,
  Search,
  Trash2,
  Pin,
  PinOff
} from "lucide-react";

import {
  getNotes,
  createNote,
  updateNote,
  deleteNote
} from "../services/api";

import NoteEditor from "./NoteEditor";


export default function StickyNotesModal({
  open,
  onClose
}) {

  const [
    notes,
    setNotes
  ] = useState([]);

  const [
    selectedId,
    setSelectedId
  ] = useState(null);

  const [
    search,
    setSearch
  ] = useState("");

  const [
    loading,
    setLoading
  ] = useState(false);

  const [
    page,
    setPage
  ] = useState(1);

  const PAGE_SIZE = 10;

  useEffect(() => {
    setPage(1);
  }, [search]);



  useEffect(() => {

    if (!open) return;

    loadNotes();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  async function loadNotes() {

    try {

      setLoading(true);

      const response =
        await getNotes();

      const data =
        response.data || [];

      setNotes(data);

      if (
        data.length &&
        !selectedId
      ) {

        setSelectedId(
          data[0]._id
        );

      }

    } finally {

      setLoading(false);

    }

  }


  async function handleNewNote() {

    const response =
      await createNote({
        title:
          "",
        name:
          "",
        content:
          ""
      });

    const note =
      response.data;

    setNotes(prev => [
      note,
      ...prev
    ]);

    setSelectedId(
      note._id
    );

  }


  async function handleDelete() {

    if (!selectedId) return;

    const confirmed =
      window.confirm(
        "Delete this note?"
      );

    if (!confirmed) return;

    await deleteNote(
      selectedId
    );

    const remaining =
      notes.filter(
        note =>
          note._id !==
          selectedId
      );

    setNotes(
      remaining
    );

    setSelectedId(
      remaining[0]?._id ||
      null
    );

  }


  async function handleNoteChange(
    id,
    changes
  ) {

    setNotes(prev =>
      prev.map(note =>
        note._id === id
          ? {
            ...note,
            ...changes
          }
          : note
      )
    );

    try {

      await updateNote(
        id,
        changes
      );

    } catch (error) {

      console.error(
        "Auto save failed",
        error
      );

    }

  }


  async function togglePin(note) {

    await handleNoteChange(
      note._id,
      {
        pinned:
          !note.pinned
      }
    );

  }


  if (!open) return null;


  const filteredNotes =
    notes.filter(note => {

      const value =
        `${note.title} ${note.content}`
          .toLowerCase();

      return value.includes(
        search.toLowerCase()
      );

    });


  const selectedNote =
    notes.find(
      note =>
        note._id ===
        selectedId
    );


  return (

    <div
      className="notes-overlay"
      onClick={onClose}
    >

      <div
        className="notes-window"
        onClick={e =>
          e.stopPropagation()
        }
      >

        <header className="notes-header">

          <div>

            <h2>
              📝 Sticky Notes
            </h2>

            <span>
              Auto-save enabled
            </span>

          </div>


          <div className="notes-actions">

            <button
              onClick={handleNewNote}
              className="icon-button primary"
              title="New note"
            >
              <Plus size={20} />
            </button>

            <button
              onClick={onClose}
              className="icon-button"
            >
              <X size={20} />
            </button>

          </div>

        </header>


        <div className="notes-body">

          <aside className="notes-sidebar">

            <div className="notes-search">

              <Search size={16} />

              <input
                value={search}
                onChange={e =>
                  setSearch(
                    e.target.value
                  )
                }
                placeholder="Search notes..."
              />

            </div>





            <div className="notes-list">

              {loading && (
                <div className="empty">
                  Loading...
                </div>
              )}


              {!loading &&
                filteredNotes.length === 0 && (
                  <div className="empty">
                    No notes yet.
                  </div>
                )}


              {filteredNotes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(
                note => (

                  <button
                    key={note._id}
                    className={
                      "note-list-item " +
                      (
                        selectedId ===
                          note._id
                          ? "active"
                          : ""
                      )
                    }
                    onClick={() =>
                      setSelectedId(
                        note._id
                      )
                    }
                  >

                    <span
                      className={
                        `note-dot ${note.color}`
                      }
                    />

                    <div>

                      <strong>
                        {
                          note.name ||
                          note.title ||
                          "Unnamed Note"
                        }
                      </strong>



                      <small>
                        {
                          stripHtml(
                            note.content
                          ).slice(
                            0,
                            60
                          ) ||
                          "Empty note"
                        }
                      </small>

                    </div>

                    {note.pinned && (
                      <Pin
                        size={13}
                      />
                    )}

                  </button>

                )
              )}

              {filteredNotes.length > PAGE_SIZE && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                  <button 
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    style={{ padding: '5px 10px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px', background: '#f5f5f5' }}
                  >
                    Prev
                  </button>
                  <span style={{ fontSize: '0.9em', alignSelf: 'center' }}>
                    Page {page} of {Math.ceil(filteredNotes.length / PAGE_SIZE)}
                  </span>
                  <button 
                    disabled={page === Math.ceil(filteredNotes.length / PAGE_SIZE)}
                    onClick={() => setPage(p => p + 1)}
                    style={{ padding: '5px 10px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px', background: '#f5f5f5' }}
                  >
                    Next
                  </button>
                </div>
              )}

            </div>

          </aside>


          <main className="note-main">

            {!selectedNote ? (

              <div className="empty-main">

                <div>
                  📝
                </div>

                <h3>
                  Create your first note
                </h3>

                <button
                  onClick={
                    handleNewNote
                  }
                >
                  <Plus size={16} />
                  New Note
                </button>

              </div>

            ) : (

              <>

                <div className="note-toolbar">

                  <input
                    className="note-title"
                    value={
                      selectedNote.title
                    }
                    onChange={e =>
                      handleNoteChange(
                        selectedNote._id,
                        {
                          title:
                            e.target.value
                        }
                      )
                    }
                    placeholder="Note title"
                  />

                  <input
                    className="note-name"
                    value={selectedNote.name || ''}
                    onChange={e => handleNoteChange(selectedNote._id, { name: e.target.value })}
                    placeholder="Name"
                    style={{ marginLeft: '10px', padding: '5px 10px', border: 'none', borderBottom: '1px solid #eee', fontSize: '14px', outline: 'none' }}
                  />


                  <div>

                    <button
                      className="icon-button"
                      onClick={() =>
                        togglePin(
                          selectedNote
                        )
                      }
                    >
                      {selectedNote.pinned
                        ? <PinOff size={17} />
                        : <Pin size={17} />
                      }
                    </button>


                    <button
                      className="icon-button danger"
                      onClick={
                        handleDelete
                      }
                    >
                      <Trash2 size={17} />
                    </button>

                  </div>

                </div>


                <NoteEditor
                  note={
                    selectedNote
                  }
                  onChange={(
                    changes
                  ) =>
                    handleNoteChange(
                      selectedNote._id,
                      changes
                    )
                  }
                />

              </>

            )}

          </main>

        </div>

      </div>

    </div>
  );
}


function stripHtml(html = "") {

  const div =
    document.createElement(
      "div"
    );

  div.innerHTML = html;

  return (
    div.textContent ||
    div.innerText ||
    ""
  );

}